import { type ReactNode } from 'react'
import { Head, router, useForm } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import RichTextEditor from '@/components/RichTextEditor'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { AdminPageDetail } from '@/types'

type Props = {
  page: AdminPageDetail
}

export default function AdminPagesEdit({ page }: Props) {
  const { data, setData, patch, processing, errors } = useForm({
    title: page.title,
    body: page.body || '',
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(`/admin/pages/${page.id}`)
  }

  return (
    <>
      <Head title={`Admin - Edit ${page.title}`} />
      <div>
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-semibold tracking-tight font-serif">Edit Page</h1>
          <button
            type="button"
            onClick={() => router.visit('/admin/pages')}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Back to Pages
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={data.title}
              onChange={e => setData('title', e.target.value)}
              className="mt-1.5"
            />
            {errors.title && <p className="mt-1 text-sm text-destructive">{errors.title}</p>}
          </div>

          <div>
            <p className="text-sm text-muted-foreground">
              URL: <span className="font-mono">/{page.slug}</span>
            </p>
          </div>

          <div>
            <Label>Content</Label>
            <div className="mt-1.5">
              <RichTextEditor
                content={data.body}
                onChange={html => setData('body', html)}
              />
            </div>
            {errors.body && <p className="mt-1 text-sm text-destructive">{errors.body}</p>}
          </div>

          <Button type="submit" disabled={processing}>
            Save Changes
          </Button>
        </form>
      </div>
    </>
  )
}

AdminPagesEdit.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
